import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchSafetyRequests,
  submitSafetyRequest,
  type ParticipationStatus,
  type SafetyRequest,
  type SafetyRequestKind,
} from '../lib/api';
import {
  clearFailedSafetyDelivery,
  readFailedSafetyDeliveries,
  type FailedSafetyDelivery,
} from '../lib/outbox';
import { useModalFocus } from '../lib/useModalFocus';

function kindLabel(kind: SafetyRequestKind): string {
  if (kind === 'appeal') return 'Moderation appeal';
  if (kind === 'privacy') return 'Privacy or data-rights request';
  return 'Complaint';
}

type SafetySheetProps = {
  open: boolean;
  onClose: () => void;
  initialKind?: SafetyRequestKind;
  participationStatus: ParticipationStatus | null;
  requests: SafetyRequest[];
  failedRequests: FailedSafetyDelivery[];
  deliveryNotice?: string | null;
  onRequestsChange: (requests: SafetyRequest[]) => void;
  onFailedRequestsChange: (requests: FailedSafetyDelivery[]) => void;
};

export function SafetySheet(props: SafetySheetProps) {
  if (!props.open) return null;
  return <SafetySheetDialog {...props} />;
}

function SafetySheetDialog({
  open,
  onClose,
  initialKind = 'complaint',
  participationStatus,
  requests,
  failedRequests,
  deliveryNotice,
  onRequestsChange,
  onFailedRequestsChange,
}: SafetySheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [kind, setKind] = useState<SafetyRequestKind>(initialKind);
  const [body, setBody] = useState('');
  const [notice, setNotice] = useState<string | null>(deliveryNotice ?? null);
  const [retryingReference, setRetryingReference] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  useModalFocus(ref, onClose);

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    if (deliveryNotice) setNotice(deliveryNotice);
  }, [deliveryNotice, initialKind, open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (body.trim().length < 20 || submitting) return;
    setSubmitting(true);
    const result = await submitSafetyRequest(
      kind,
      body,
      kind === 'appeal' ? participationStatus?.sanction?.id : null,
    );
    setSubmitting(false);
    if (result.ok) {
      if (retryingReference) {
        await clearFailedSafetyDelivery(retryingReference);
        onFailedRequestsChange(readFailedSafetyDeliveries());
        setRetryingReference(null);
      }
      setBody('');
      setNotice(result.queued
        ? `Saved privately and queued for delivery. Reference ${result.reference}.`
        : `Sent privately. Reference ${result.reference}.`);
      onRequestsChange(await fetchSafetyRequests());
      return;
    }
    setNotice(result.error === 'rate-limited'
      ? 'Too many requests — try again later.'
      : result.error === 'offline'
        ? 'You’re offline — your text remains here so you can try again when connected.'
        : 'Add at least 20 characters and try again.');
  };

  return createPortal(
    <div className="participation-sheet-backdrop safety-sheet-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className="participation-sheet safety-sheet"
        id="safety-appeal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-sheet-title"
        aria-describedby="safety-sheet-description"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="safety-sheet-top">
          <div>
            <span className="participation-sheet-kicker">Private supporter care</span>
            <h2 id="safety-sheet-title">Help &amp; safety</h2>
          </div>
          <button type="button" className="safety-sheet-close" onClick={onClose} aria-label="Close help and safety">Close</button>
        </div>
        <p id="safety-sheet-description">
          Flag a specific take from its card. Use this private route for a wider complaint, an appeal, or a privacy request.
        </p>

        <form className="safety-sheet-form" onSubmit={submit}>
          <label>
            <span>Request type</span>
            <select value={kind} onChange={(event) => setKind(event.currentTarget.value as SafetyRequestKind)}>
              <option value="complaint">Complaint</option>
              <option value="appeal">Moderation appeal</option>
              <option value="privacy">Privacy or data-rights request</option>
            </select>
          </label>
          <label>
            <span>What happened?</span>
            <textarea
              value={body}
              minLength={20}
              maxLength={2000}
              onChange={(event) => setBody(event.currentTarget.value)}
              placeholder="Give enough detail for a moderator to understand the issue."
            />
          </label>
          <button className="safety-submit" type="submit" disabled={body.trim().length < 20 || submitting}>
            {submitting ? 'Sending…' : 'Send privately'}
          </button>
        </form>

        {notice ? <p role="status" className="safety-status" aria-live="polite">{notice}</p> : null}

        {requests.length > 0 ? (
          <section className="safety-request-list" aria-label="Your private requests">
            <strong>Your requests on this device</strong>
            {requests.map((request) => (
              <article key={request.id}>
                <span>{kindLabel(request.kind)} · {request.status} · {request.id.slice(0, 8)}</span>
                <p>{request.body}</p>
                {request.reviewer_note ? <p className="safety-reviewer-note"><b>Reviewer:</b> {request.reviewer_note}</p> : null}
              </article>
            ))}
          </section>
        ) : null}

        {failedRequests.length > 0 ? (
          <section className="safety-request-list" aria-label="Private requests needing another attempt">
            <strong>Not delivered — saved on this device</strong>
            {failedRequests.map((request) => (
              <article key={request.reference}>
                <span>{kindLabel(request.kind)} · needs another attempt · {request.reference.slice(0, 8)}</span>
                <p>{request.body}</p>
                <button type="button" onClick={() => {
                  setKind(request.kind);
                  setBody(request.body);
                  setRetryingReference(request.reference);
                  setNotice(`Loaded saved request ${request.reference.slice(0, 8)}. Review it, then send privately again.`);
                }}>Review and try again</button>
              </article>
            ))}
          </section>
        ) : null}

        <p className="safety-links">
          <a href="/community-safety.html" target="_blank" rel="noreferrer">How moderation works</a>
          {' · '}
          <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
        </p>
      </div>
    </div>,
    document.body,
  );
}
